import { useEffect, useState } from "react";
import { X, Copy, Check, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Share modal: pick a lead, add a note, mint a share link.
 * Creates a one-item collection (the passed-in property). For multi-property
 * shares, agents use the collections page (v2). Keeps the MVP quick.
 */
export default function ShareModal({ property, onClose }) {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [note, setNote] = useState("");
  const [name, setName] = useState(property?.address_line1 || "For you");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { shareUrl }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/leads").then(r => setLeads(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!leadId) return toast.error("Pick a lead first");
    setBusy(true);
    try {
      const r = await api.post("/collections", {
        lead_id: leadId,
        name: name || property.address_line1,
        note: note || null,
        property_ids: [property.id],
      });
      const shareUrl = `${window.location.origin}/share/${r.data.share_token}`;
      setResult({ shareUrl });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(result.shareUrl);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8" role="dialog" aria-label="Share property" data-testid="share-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Share property</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="share-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!result ? (
          <div className="p-6 space-y-4">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
              {property.hero_image_url && <img src={property.hero_image_url} alt="" className="h-12 w-16 rounded object-cover" />}
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{property.address_line1}</div>
                {property.list_price && <div className="text-xs text-slate-500 tabular-nums">${Number(property.list_price).toLocaleString()}</div>}
              </div>
            </div>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Share with lead</span>
              <select value={leadId} onChange={e => setLeadId(e.target.value)} className="input" data-testid="share-lead-select">
                <option value="">Select a lead…</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.full_name} · {l.stage}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Collection name</span>
              <input value={name} onChange={e => setName(e.target.value)} className="input" data-testid="share-name" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Personal note <span className="text-slate-400 normal-case font-normal">(optional)</span></span>
              <textarea value={note} onChange={e => setNote(e.target.value)} className="input min-h-[60px]" placeholder="Thought this might fit what you're looking for…" />
            </label>

            <div className="flex items-center justify-end pt-2">
              <button onClick={submit} disabled={busy || !leadId} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="share-submit-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} Mint share link
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-md border-l-4 border-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900">
              Share link ready. Text it, email it, or paste it wherever you talk to this lead.
            </div>
            <div className="flex items-stretch gap-2">
              <input readOnly value={result.shareUrl} className="input flex-1 font-mono text-xs" data-testid="share-url-output" />
              <button onClick={copy} className="inline-flex items-center gap-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 text-sm font-semibold" data-testid="share-copy-btn">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-end">
              <button onClick={onClose} className="text-sm font-semibold text-slate-600 hover:text-slate-900">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
